// frontendCFCalculations.ts
// Frontend Cash Flow calculation service that mirrors UnifiedDetailedCashFlow component logic
import { AccountEntry, CategoryTemplate, ViewType } from '../../types';
import { compareMonthYear } from './calculations';

// Interface matching the backend CFLineItem structure
export interface CFLineItem {
  key: string;
  name: string;
  values: (number | null)[];
}

export interface CFSection {
  key: string;
  name: string;
  line_items: CFLineItem[];
}

export interface CFDetailData {
  periods: string[];
  sections: CFSection[];
}

export class FrontendCFCalculationService {
  /**
   * Build Cash Flow data using frontend calculations that mirror the component's logic
   */
  static buildCFData(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    viewType: ViewType,
    selectedProject: any
  ): CFDetailData {
    if (!accounts.length || !activeTemplate) {
      return { periods: [], sections: [] };
    }

    // Get all available months from accounts
    const allMonths = [...new Set(accounts.map(acc => acc.month))].sort(compareMonthYear);

    // Always generate monthly data - let the component handle view transformations
    const periods = allMonths;

    const sections: CFSection[] = [];

    // Operating Activities Section
    sections.push(this.buildOperatingActivitiesSection(accounts, activeTemplate, periods));

    // Investing Activities Section  
    sections.push(this.buildInvestingActivitiesSection(accounts, activeTemplate, periods));

    // Financing Activities Section
    sections.push(this.buildFinancingActivitiesSection(accounts, activeTemplate, periods));

    // Cash Roll-Forward Section
    sections.push(this.buildCashRollForwardSection(accounts, activeTemplate, periods, sections));

    return {
      periods,
      sections
    };
  }

  /**
   * Build Operating Activities section
   */
  private static buildOperatingActivitiesSection(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    periods: string[]
  ): CFSection {
    const line_items: CFLineItem[] = [];

    // Net Income (from P&L)
    const netIncomeValues = periods.map(period => 
      this.calculateNetIncome(accounts, activeTemplate, period)
    );

    line_items.push({
      key: 'net_income',
      name: 'Net Income',
      values: netIncomeValues
    });

    // Add non-cash items (depreciation, amortization, etc.) - matches legacy nonCashRows logic
    const nonCashCategories = this.getOrderedNonCashCategories(activeTemplate);
    
    nonCashCategories.forEach(categoryObj => {
      if (typeof categoryObj.value === 'string') {
        const nonCashValues = periods.map(period => 
          this.getCategoryBalance(accounts, categoryObj.value, period, activeTemplate)
        );

        if (nonCashValues.some(val => Math.abs(val || 0) > 0.01)) {
          line_items.push({
            key: `add_${categoryObj.value}`,
            name: `Add: ${categoryObj.label}`,
            values: nonCashValues
          });
        }
      }
    });

    // Working Capital Changes (from Balance Sheet changes)
    const workingCapitalItems = this.buildWorkingCapitalChanges(accounts, activeTemplate, periods);
    line_items.push(...workingCapitalItems);

    // Calculate section total
    const sectionTotalValues = periods.map((period, periodIndex) => {
      return line_items.reduce((total, item) => {
        if (item.key === 'section_total') return total;
        const value = item.values[periodIndex];
        return total + (value || 0);
      }, 0);
    });

    line_items.push({
      key: 'section_total',
      name: 'Net Cash from Operating Activities',
      values: sectionTotalValues
    });

    return {
      key: 'net_cash_from_operating_activities',
      name: 'Cash Flow from Operating Activities',
      line_items
    };
  }

  /**
   * Build Investing Activities section
   */
  private static buildInvestingActivitiesSection(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    periods: string[]
  ): CFSection {
    const line_items: CFLineItem[] = [];

    // Capital Expenditures (PPE changes)
    const capexValues = periods.map((period, index) => 
      this.calculateCapitalExpenditures(accounts, activeTemplate, period, index, periods)
    );

    // Always include CapEx line item (like legacy code) - show even if zero
    line_items.push({
      key: 'capex_ppe',
      name: 'Capital Expenditures (PPE)',
      values: capexValues
    });

    // Asset Sales/Dispositions (always zero in legacy, but include for completeness)
    const assetSalesValues = periods.map(() => 0);

    line_items.push({
      key: 'asset_sales_dispositions',
      name: 'Asset Sales/Dispositions (Net)',
      values: assetSalesValues
    });

    // Other Investing Activities (always zero in legacy, but include for completeness)
    const otherInvestingValues = periods.map(() => 0);

    line_items.push({
      key: 'other_investing_activities', 
      name: 'Other Investing Activities',
      values: otherInvestingValues
    });

    // Calculate section total
    const sectionTotalValues = periods.map((period, periodIndex) => {
      return line_items.reduce((total, item) => {
        if (item.key === 'section_total') return total;
        const value = item.values[periodIndex];
        return total + (value || 0);
      }, 0);
    });

    line_items.push({
      key: 'section_total',
      name: 'Net Cash from Investing Activities',
      values: sectionTotalValues
    });

    return {
      key: 'net_cash_from_investing_activities',
      name: 'Cash Flow from Investing Activities',
      line_items
    };
  }

  /**
   * Build Financing Activities section
   */
  private static buildFinancingActivitiesSection(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    periods: string[]
  ): CFSection {
    const line_items: CFLineItem[] = [];

    // Debt Raise/Paydown (debt changes)
    const debtChangeValues = periods.map((period, index) => 
      this.calculateDebtChanges(accounts, activeTemplate, period, index, periods)
    );

    // Always include Debt line item (like legacy code) - show even if zero
    line_items.push({
      key: 'debt_raise_paydown',
      name: 'Debt Raise/Paydown (Net)',
      values: debtChangeValues
    });

    // Equity Raise/Repurchase (equity changes)
    const equityChangeValues = periods.map((period, index) => 
      this.calculateEquityChanges(accounts, activeTemplate, period, index, periods)
    );

    // Always include Equity line item (like legacy code) - show even if zero
    line_items.push({
      key: 'equity_raise_repurchase',
      name: 'Equity Raise/Repurchase (Net)',
      values: equityChangeValues
    });

    // Other Financing Activities (always zero in legacy, but include for completeness)
    const otherFinancingValues = periods.map(() => 0);

    line_items.push({
      key: 'other_financing_activities',
      name: 'Other Financing Activities',
      values: otherFinancingValues
    });

    // Calculate section total
    const sectionTotalValues = periods.map((period, periodIndex) => {
      return line_items.reduce((total, item) => {
        if (item.key === 'section_total') return total;
        const value = item.values[periodIndex];
        return total + (value || 0);
      }, 0);
    });

    line_items.push({
      key: 'section_total',
      name: 'Net Cash from Financing Activities',
      values: sectionTotalValues
    });

    return {
      key: 'net_cash_from_financing_activities',
      name: 'Cash Flow from Financing Activities',
      line_items
    };
  }

  /**
   * Build Cash Roll-Forward section
   */
  private static buildCashRollForwardSection(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    periods: string[],
    otherSections: CFSection[]
  ): CFSection {
    const line_items: CFLineItem[] = [];

    // Calculate Net Change in Cash (sum of all other sections)
    const netChangeValues = periods.map((period, periodIndex) => {
      return otherSections.reduce((total, section) => {
        const sectionTotal = section.line_items.find(item => item.key === 'section_total');
        if (sectionTotal && sectionTotal.values[periodIndex] !== null) {
          return total + (sectionTotal.values[periodIndex] || 0);
        }
        return total;
      }, 0);
    });

    // Cash on Balance Sheet (ending cash for each period)
    const cashOnBSValues = periods.map(period => 
      this.getCashOnBalanceSheet(accounts, activeTemplate, period)
    );

    // Calculate Beginning Cash
    const beginningCashValues = periods.map((period, index) => {
      if (index === 0) {
        // First period: Calculate as plug if missing opening balances
        if (this.hasMissingOpeningBalances(accounts, periods)) {
          const cashOnBS = cashOnBSValues[index];
          const netChange = netChangeValues[index];
          if (cashOnBS !== null && netChange !== null) {
            return cashOnBS - netChange;
          }
        }
        // If we have opening data, get previous period cash
        const previousPeriod = this.getPreviousPeriod(period);
        return this.getCashOnBalanceSheet(accounts, activeTemplate, previousPeriod);
      } else {
        // Subsequent periods: use previous period's ending cash
        return cashOnBSValues[index - 1];
      }
    });

    // Calculate Ending Cash (Roll-Forward)
    const endingCashValues = periods.map((period, index) => {
      const beginningCash = beginningCashValues[index];
      const netChange = netChangeValues[index];
      if (beginningCash !== null && netChange !== null) {
        return beginningCash + netChange;
      }
      return null;
    });

    // Check (Ending Cash - Balance Sheet)
    const checkValues = periods.map((period, index) => {
      const endingCash = endingCashValues[index];
      const cashOnBS = cashOnBSValues[index];
      if (endingCash !== null && cashOnBS !== null) {
        return endingCash - cashOnBS;
      }
      return null;
    });

    // Add all line items
    line_items.push({
      key: 'beginning_cash',
      name: 'Beginning Cash',
      values: beginningCashValues
    });

    line_items.push({
      key: 'net_change_in_cash',
      name: 'Net Change in Cash',
      values: netChangeValues
    });

    line_items.push({
      key: 'ending_cash_rollforward',
      name: 'Ending Cash (Roll-Forward)',
      values: endingCashValues
    });

    line_items.push({
      key: 'cash_on_balance_sheet',
      name: 'Cash on Balance Sheet',
      values: cashOnBSValues
    });

    line_items.push({
      key: 'check_ending_cash_vs_balance_sheet',
      name: 'Check (Ending Cash - Balance Sheet)',
      values: checkValues
    });

    return {
      key: 'cash_roll_forward',
      name: 'Cash Roll-Forward',
      line_items
    };
  }

  /**
   * Build working capital changes (balance sheet dependent)
   */
  private static buildWorkingCapitalChanges(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    periods: string[]
  ): CFLineItem[] {
    const items: CFLineItem[] = [];
    const hasMissingOpening = this.hasMissingOpeningBalances(accounts, periods);

    // Change in Trade Receivables (nwcRole = 'accounts-receivable')
    const receivablesChangeValues = periods.map((period, index) => {
      if (hasMissingOpening && index === 0) return null; // N/A
      return this.calculateNWCRoleChange(accounts, activeTemplate, 'accounts-receivable', period, index, periods);
    });

    if (receivablesChangeValues.some(val => val !== null)) {
      items.push({
        key: 'change_in_trade_receivables',
        name: 'Change in Trade Receivables',
        values: receivablesChangeValues
      });
    }

    // Change in Inventory (nwcRole = 'inventory')
    const inventoryChangeValues = periods.map((period, index) => {
      if (hasMissingOpening && index === 0) return null; // N/A
      return this.calculateNWCRoleChange(accounts, activeTemplate, 'inventory', period, index, periods);
    });

    if (inventoryChangeValues.some(val => val !== null)) {
      items.push({
        key: 'change_in_inventory',
        name: 'Change in Inventory',
        values: inventoryChangeValues
      });
    }

    // Change in Other Current Assets (excl. cash, AR, inventory, debt-like, non-cash)
    const otherCurrentAssetsChangeValues = periods.map((period, index) => {
      if (hasMissingOpening && index === 0) return null; // N/A
      return this.calculateOtherCurrentChange(accounts, activeTemplate, 'asset', period, index, periods);
    });

    if (otherCurrentAssetsChangeValues.some(val => val !== null)) {
      items.push({
        key: 'change_in_other_current_assets',
        name: 'Change in Other Current Assets (excl. non-cash)',
        values: otherCurrentAssetsChangeValues
      });
    }

    // Change in Trade Payables (nwcRole = 'accounts-payable')
    const payablesChangeValues = periods.map((period, index) => {
      if (hasMissingOpening && index === 0) return null; // N/A
      return this.calculateNWCRoleChange(accounts, activeTemplate, 'accounts-payable', period, index, periods);
    });

    if (payablesChangeValues.some(val => val !== null)) {
      items.push({
        key: 'change_in_trade_payables',
        name: 'Change in Trade Payables',
        values: payablesChangeValues
      });
    }

    // Change in Other Current Liabilities (excl. AP, short-term debt, debt-like, non-cash)
    const otherCurrentLiabilitiesChangeValues = periods.map((period, index) => {
      if (hasMissingOpening && index === 0) return null; // N/A
      return this.calculateOtherCurrentChange(accounts, activeTemplate, 'liability', period, index, periods);
    });

    if (otherCurrentLiabilitiesChangeValues.some(val => val !== null)) {
      items.push({
        key: 'change_in_other_current_liabilities',
        name: 'Change in Other Current Liabilities (excl. non-cash)',
        values: otherCurrentLiabilitiesChangeValues
      });
    }

    return items;
  }

  /**
   * Calculate Net Income from P&L (reuse from other services)
   */
  private static calculateNetIncome(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string
  ): number {
    const monthAccounts = accounts.filter(account => account.month === period);
    
    // Get revenue categories
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue')
      .map(cat => cat.value);
    
    // Get contra-revenue categories
    const contraRevenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'contra-revenue')
      .map(cat => cat.value);
    
    // Get expense categories (all expense types)
    const expenseCategories = activeTemplate.categories
      .filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 
                      'income-tax-expense', 'other-expense'].includes(cat.type))
      .map(cat => cat.value);
    
    // Calculate revenue
    const revenue = monthAccounts
      .filter(account => revenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.credit - account.debit), 0);
    
    // Calculate contra-revenue
    const contraRevenue = monthAccounts
      .filter(account => contraRevenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Calculate expenses
    const expenses = monthAccounts
      .filter(account => expenseCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Net Income = Revenue - Contra Revenue - Expenses
    return revenue - contraRevenue - expenses;
  }


  /**
   * Calculate working capital changes for specific NWC roles (AR, Inventory, AP)
   */
  private static calculateNWCRoleChange(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    nwcRole: string,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    if (periodIndex === 0 && this.hasMissingOpeningBalances(accounts, periods)) {
      return null; // N/A for first period when missing opening balances
    }

    const endingBalance = this.getNWCRoleBalance(accounts, activeTemplate, nwcRole, period);
    const beginningBalance = periodIndex === 0 
      ? this.getNWCRoleBalance(accounts, activeTemplate, nwcRole, this.getPreviousPeriod(period))
      : this.getNWCRoleBalance(accounts, activeTemplate, nwcRole, periods[periodIndex - 1]);

    if (endingBalance !== null && beginningBalance !== null) {
      const change = endingBalance - beginningBalance;
      // For assets (AR, Inventory): increase is negative cash flow (cash used)
      // For liabilities (AP): increase is positive cash flow (cash provided)
      const isAssetRole = ['accounts-receivable', 'inventory'].includes(nwcRole);
      return isAssetRole ? -change : change;
    }

    return null;
  }

  /**
   * Calculate changes for "Other Current" assets/liabilities (excluding specific roles and non-cash)
   */
  private static calculateOtherCurrentChange(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    type: 'asset' | 'liability',
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    if (periodIndex === 0 && this.hasMissingOpeningBalances(accounts, periods)) {
      return null; // N/A for first period when missing opening balances
    }

    const endingBalance = this.getOtherCurrentBalance(accounts, activeTemplate, type, period);
    const beginningBalance = periodIndex === 0 
      ? this.getOtherCurrentBalance(accounts, activeTemplate, type, this.getPreviousPeriod(period))
      : this.getOtherCurrentBalance(accounts, activeTemplate, type, periods[periodIndex - 1]);

    if (endingBalance !== null && beginningBalance !== null) {
      const change = endingBalance - beginningBalance;
      // For assets: increase is negative cash flow (cash used)
      // For liabilities: increase is positive cash flow (cash provided)
      return type === 'asset' ? -change : change;
    }

    return null;
  }

  /**
   * Calculate capital expenditures (PPE changes) - matches legacy calcSumChange logic
   */
  private static calculateCapitalExpenditures(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    if (periodIndex === 0 && this.hasMissingOpeningBalances(accounts, periods)) {
      return null; // N/A for first period when missing opening balances
    }

    const previousPeriod = periodIndex === 0 
      ? this.getPreviousPeriod(period)
      : periods[periodIndex - 1];

    if (!previousPeriod) return null;

    // Get PPE categories (using isPpe flag like legacy code)
    const ppeCategories = activeTemplate.categories
      .filter(cat => cat.isPpe)
      .map(cat => cat.value);

    if (ppeCategories.length === 0) return null;

    // Calculate change in PPE balances (legacy calcSumChange logic)
    let change = 0;
    for (const category of ppeCategories) {
      const currentBalance = this.getCategoryBalance(accounts, category, period, activeTemplate);
      const previousBalance = this.getCategoryBalance(accounts, category, previousPeriod, activeTemplate);
      change += (currentBalance - previousBalance);
    }

    // Add back depreciation expense (adjustForDepreciation = true in legacy)
    const depreciationExpense = this.getDepreciationExpense(accounts, activeTemplate, period);
    change += depreciationExpense;

    // Negative because CapEx is cash outflow
    return -change;
  }

  /**
   * Calculate asset sales/dispositions
   */
  private static calculateAssetSales(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    // Simplified implementation - would need more complex logic for actual asset sales
    return null;
  }

  /**
   * Calculate debt changes - matches legacy calcSumChange logic
   */
  private static calculateDebtChanges(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    if (periodIndex === 0 && this.hasMissingOpeningBalances(accounts, periods)) {
      return null; // N/A for first period when missing opening balances
    }

    const previousPeriod = periodIndex === 0 
      ? this.getPreviousPeriod(period)
      : periods[periodIndex - 1];

    if (!previousPeriod) return null;

    // Get debt categories (using isDebt flag like legacy code)
    const debtCategories = activeTemplate.categories
      .filter(cat => cat.isDebt)
      .map(cat => cat.value);

    if (debtCategories.length === 0) return null;

    // Calculate change in debt balances (legacy calcSumChange logic)
    let change = 0;
    for (const category of debtCategories) {
      const currentBalance = this.getCategoryBalance(accounts, category, period, activeTemplate);
      const previousBalance = this.getCategoryBalance(accounts, category, previousPeriod, activeTemplate);
      change += (currentBalance - previousBalance);
    }

    // Positive = debt raised (cash inflow), Negative = debt paid down (cash outflow)
    return change;
  }

  /**
   * Calculate equity changes - matches legacy calcSumChange logic
   */
  private static calculateEquityChanges(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    if (periodIndex === 0 && this.hasMissingOpeningBalances(accounts, periods)) {
      return null; // N/A for first period when missing opening balances
    }

    const previousPeriod = periodIndex === 0 
      ? this.getPreviousPeriod(period)
      : periods[periodIndex - 1];

    if (!previousPeriod) return null;

    // Get equity categories (using isEquityCapital flag like legacy code)
    const equityCategories = activeTemplate.categories
      .filter(cat => cat.isEquityCapital)
      .map(cat => cat.value);

    if (equityCategories.length === 0) return null;

    // Calculate change in equity balances (legacy calcSumChange logic)
    let change = 0;
    for (const category of equityCategories) {
      const currentBalance = this.getCategoryBalance(accounts, category, period, activeTemplate);
      const previousBalance = this.getCategoryBalance(accounts, category, previousPeriod, activeTemplate);
      change += (currentBalance - previousBalance);
    }

    // Note: Legacy doesn't exclude net income here - that's handled by using isEquityCapital flag
    // which should exclude retained earnings where net income flows
    return change; // Positive = equity raised, Negative = equity repurchased
  }

  /**
   * Calculate other financing activities
   */
  private static calculateOtherFinancingActivities(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    periodIndex: number,
    periods: string[]
  ): number | null {
    // Simplified implementation - would include dividends, other financing changes
    return null;
  }

  /**
   * Get cash on balance sheet
   */
  private static getCashOnBalanceSheet(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string
  ): number | null {
    return this.getNWCRoleBalance(accounts, activeTemplate, 'cash', period);
  }

  /**
   * Get balance for accounts with specific NWC role
   */
  private static getNWCRoleBalance(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    nwcRole: string,
    period: string
  ): number | null {
    if (!period) return null;

    // Get categories with this NWC role
    const relevantCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === nwcRole)
      .map(cat => cat.value);

    if (relevantCategories.length === 0) return null;

    const relevantAccounts = accounts.filter(account => 
      account.month === period && relevantCategories.includes(account.category)
    );

    if (relevantAccounts.length === 0) return null;

    // Calculate balance based on account type
    const firstCategory = activeTemplate.categories.find(cat => cat.nwcRole === nwcRole);
    const isAsset = firstCategory?.type?.includes('asset');
    
    return relevantAccounts.reduce((total, account) => {
      return total + (isAsset 
        ? (account.debit - account.credit) 
        : (account.credit - account.debit));
    }, 0);
  }

  /**
   * Get balance for "Other Current" assets/liabilities (excluding specific NWC roles and non-cash)
   */
  private static getOtherCurrentBalance(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    type: 'asset' | 'liability',
    period: string
  ): number | null {
    if (!period) return null;

    // Replicate the legacy logic from reportBuilder.ts
    let relevantCategories: string[];
    
    if (type === 'asset') {
      // current-asset AND exclude cash, AR, inventory, debt-like, non-cash
      relevantCategories = activeTemplate.categories.filter(cat => 
        cat.type === 'current-asset' && 
        cat.nwcRole !== 'cash' && 
        cat.nwcRole !== 'accounts-receivable' && 
        cat.nwcRole !== 'inventory' && 
        cat.nwcRole !== 'debt-like' && 
        !cat.isNonCash
      ).map(cat => cat.value);
    } else {
      // current-liability AND exclude AP, short-term debt, debt-like, non-cash
      relevantCategories = activeTemplate.categories.filter(cat => 
        cat.type === 'current-liability' && 
        cat.nwcRole !== 'accounts-payable' && 
        cat.nwcRole !== 'short-term-debt' && 
        cat.nwcRole !== 'debt-like' && 
        !cat.isNonCash
      ).map(cat => cat.value);
    }

    if (relevantCategories.length === 0) return null;

    const relevantAccounts = accounts.filter(account => 
      account.month === period && relevantCategories.includes(account.category)
    );

    if (relevantAccounts.length === 0) return null;

    // Calculate balance (debit - credit for assets, credit - debit for liabilities)
    const isAsset = type === 'asset';
    
    return relevantAccounts.reduce((total, account) => {
      return total + (isAsset 
        ? (account.debit - account.credit) 
        : (account.credit - account.debit));
    }, 0);
  }

  /**
   * Get balance sheet value for a specific account type and period
   */
  private static getBalanceSheetValue(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    accountType: string,
    period: string,
    excludeNonCash: boolean = false
  ): number | null {
    if (!period) return null;

    // Map account types to category types
    const typeMapping: Record<string, string[]> = {
      'cash': ['current-asset'], // Look for cash in current assets
      'trade_receivables': ['current-asset'],
      'inventory': ['current-asset'],
      'other_current_assets': ['current-asset'],
      'ppe': ['non-current-asset'],
      'trade_payables': ['current-liability'],
      'other_current_liabilities': ['current-liability'],
      'debt': ['current-liability', 'non-current-liability'],
      'equity': ['equity']
    };

    const relevantTypes = typeMapping[accountType] || [];
    const relevantCategories = activeTemplate.categories
      .filter(cat => relevantTypes.includes(cat.type))
      .map(cat => cat.value);

    const relevantAccounts = accounts.filter(account => 
      account.month === period && relevantCategories.includes(account.category)
    );

    // For specific account types, filter further by account name
    let filteredAccounts = relevantAccounts.filter(account => {
      const accountName = account.name?.toLowerCase() || '';
      switch (accountType) {
        case 'cash':
          return accountName.includes('cash') || accountName.includes('checking') || accountName.includes('savings');
        case 'trade_receivables':
          return accountName.includes('receivable') || accountName.includes('a/r') || accountName.includes('ar');
        case 'inventory':
          return accountName.includes('inventory');
        case 'ppe':
          return accountName.includes('property') || accountName.includes('plant') || accountName.includes('equipment') || accountName.includes('ppe');
        case 'trade_payables':
          return accountName.includes('payable') || accountName.includes('a/p') || accountName.includes('ap');
        case 'debt':
          return accountName.includes('debt') || accountName.includes('loan') || accountName.includes('note');
        default:
          return true; // For other_current_assets, other_current_liabilities, equity - include all
      }
    });

    // If excludeNonCash is true, filter out accounts with "non-cash" role tag
    if (excludeNonCash) {
      filteredAccounts = filteredAccounts.filter(account => {
        // Find the category template for this account's category
        const categoryTemplate = activeTemplate.categories.find(cat => cat.value === account.category);
        if (!categoryTemplate) return true; // Include if no category template found
        
        // Check if the category has a "non-cash" role tag
        const hasNonCashRole = categoryTemplate.role_tags?.some(tag => 
          tag.toLowerCase().includes('non-cash') || tag.toLowerCase().includes('noncash')
        );
        
        return !hasNonCashRole; // Exclude if it has non-cash role tag
      });
    }

    if (filteredAccounts.length === 0) return null;

    // Calculate balance (debit - credit for assets, credit - debit for liabilities/equity)
    const isAsset = ['cash', 'trade_receivables', 'inventory', 'other_current_assets', 'ppe'].includes(accountType);
    
    return filteredAccounts.reduce((total, account) => {
      return total + (isAsset 
        ? (account.debit - account.credit) 
        : (account.credit - account.debit));
    }, 0);
  }

  /**
   * Check if missing opening balances
   */
  private static hasMissingOpeningBalances(accounts: AccountEntry[], periods: string[]): boolean {
    if (periods.length === 0) return false;
    
    const firstPeriod = periods[0];
    const previousPeriod = this.getPreviousPeriod(firstPeriod);
    
    // Check if we have any accounts for the previous period
    const previousPeriodAccounts = accounts.filter(acc => acc.month === previousPeriod);
    return previousPeriodAccounts.length === 0;
  }

  /**
   * Get previous period (simplified month calculation)
   */
  private static getPreviousPeriod(period: string): string {
    const [month, year] = period.split('-');
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
    
    if (monthIndex === 0) {
      // January -> December of previous year
      const prevYear = (parseInt(year) - 1).toString();
      return `Dec-${prevYear}`;
    } else {
      // Other months -> previous month same year
      const prevMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex - 1];
      return `${prevMonth}-${year}`;
    }
  }

  /**
   * Get ordered non-cash categories (replicates legacy getOrderedNonCashCategories logic)
   */
  private static getOrderedNonCashCategories(activeTemplate: CategoryTemplate): Array<{value: string, label: string}> {
    const ordered: Array<{value: string, label: string}> = [];
    
    // Find depreciation first
    const dep = activeTemplate.categories.find(cat => 
      cat.label.toLowerCase().includes('depreciation') && cat.isNonCash
    );
    if (dep) ordered.push({ value: dep.value, label: dep.label });
    
    // Find amortization second
    const amort = activeTemplate.categories.find(cat => 
      cat.label.toLowerCase().includes('amortization') && cat.isNonCash
    );
    if (amort) ordered.push({ value: amort.value, label: amort.label });
    
    // Find stock-based compensation third
    const sbc = activeTemplate.categories.find(cat => 
      cat.label.toLowerCase().includes('stock') && 
      cat.label.toLowerCase().includes('comp') && 
      cat.isNonCash
    );
    if (sbc) ordered.push({ value: sbc.value, label: sbc.label });
    
    // Add other non-cash categories
    const others = activeTemplate.categories.filter(cat => 
      cat.isNonCash && 
      !ordered.some(o => o.value === cat.value)
    );
    for (const cat of others) {
      ordered.push({ value: cat.value, label: cat.label });
    }
    
    return ordered;
  }

  /**
   * Get category balance for a specific period (replicates legacy sumCategory logic)
   */
  private static getCategoryBalance(
    accounts: AccountEntry[],
    category: string,
    period: string,
    activeTemplate?: CategoryTemplate
  ): number {
    if (!category || !period) return 0;
    
    const categoryAccounts = accounts.filter(account => 
      account.month === period && account.category === category
    );

    // This replicates the legacy sumCategory logic from reportBuilder.ts
    // For liability/equity categories, use credit - debit
    // For all others (assets, expenses), use debit - credit
    const categoryDef = activeTemplate?.categories.find(cat => cat.value === category);
    if (categoryDef && (categoryDef.type.includes('liability') || categoryDef.type.includes('equity'))) {
      return categoryAccounts.reduce((total, account) => {
        return total + (account.credit - account.debit);
      }, 0);
    }

    return categoryAccounts.reduce((total, account) => {
      return total + (account.debit - account.credit);
    }, 0);
  }

  /**
   * Get depreciation expense for a period (replicates legacy logic)
   */
  private static getDepreciationExpense(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string
  ): number {
    // Find depreciation categories (isNonCash + label contains "depreciation")
    const depreciationCategories = activeTemplate.categories
      .filter(cat => cat.isNonCash && cat.label.toLowerCase().includes('depreciation'))
      .map(cat => cat.value);

    if (depreciationCategories.length === 0) return 0;

    return depreciationCategories.reduce((total, category) => {
      const categoryAccounts = accounts.filter(account => 
        account.month === period && account.category === category
      );

      // For expense accounts, use debit - credit (expenses normally have debit balances)
      const categoryTotal = categoryAccounts.reduce((sum, account) => {
        return sum + (account.debit - account.credit);
      }, 0);

      return total + categoryTotal;
    }, 0);
  }
}
